import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { usersService, enrollmentsService, lessonsService, lessonProgressService } from '../config/supabase';
import '../styles/ProgressPage.css';

const ProgressPage = () => {
  const { isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    if (!isAdmin) return;
    loadUserProgress();
  }, [isAdmin]);

  const loadUserProgress = async () => {
    try {
      setLoading(true);
      const allUsers = await usersService.getAll();
      setUsers(allUsers || []);

      // Cargar progreso de TODOS los usuarios EN PARALELO
      const progressMap = {};
      const promises = (allUsers || []).map(async (usr) => {
        try {
          // Usar el método deduplicado
          const enrollments = await enrollmentsService.getUserEnrollmentsNoDup(usr.id);
          let totalLessons = 0;
          let completedLessons = 0;
          const courseProgress = {};

          // Cargar lecciones de todos los cursos EN PARALELO
          const lessonPromises = (enrollments || []).map((course) =>
            lessonsService.getByCourse(course.id).catch(() => [])
          );
          const lessonsByCourseProm = await Promise.all(lessonPromises);

          // Procesar lecciones y progreso
          const progressPromises = (enrollments || []).map((course, idx) => {
            const lessons = lessonsByCourseProm[idx] || [];
            const lessonIds = lessons.map((l) => l.id);
            return lessonProgressService.getCompleted(lessonIds)
              .then((completed) => {
                totalLessons += lessonIds.length;
                completedLessons += completed.length;
                courseProgress[course.id] = {
                  title: course.titulo,
                  total: lessonIds.length,
                  completed: completed.length,
                  pct: lessonIds.length > 0
                    ? Math.round((completed.length / lessonIds.length) * 100)
                    : 0,
                };
              })
              .catch(() => {
                courseProgress[course.id] = {
                  title: course.titulo,
                  total: 0,
                  completed: 0,
                  pct: 0,
                };
              });
          });

          await Promise.all(progressPromises);

          progressMap[usr.id] = {
            totalCourses: enrollments?.length || 0,
            totalLessons,
            completedLessons,
            overallPct: totalLessons > 0
              ? Math.round((completedLessons / totalLessons) * 100)
              : 0,
            courseProgress,
          };
        } catch (error) {
          console.error(`Error cargando progreso de ${usr.email}:`, error);
          progressMap[usr.id] = {
            totalCourses: 0,
            totalLessons: 0,
            completedLessons: 0,
            overallPct: 0,
            courseProgress: {},
          };
        }
      });

      await Promise.all(promises);
      setUserProgress(progressMap);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = (users || [])
    .filter((u) => {
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      return (
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return (a.full_name || '').localeCompare(b.full_name || '');
      }
      if (sortBy === 'progress') {
        return (userProgress[b.id]?.overallPct || 0) - (userProgress[a.id]?.overallPct || 0);
      }
      return 0;
    });

  if (!isAdmin) {
    return (
      <div className="progress-page">
        <Header />
        <div className="progress-container">
          <div className="no-progress">
            <p>⛔ No tienes permisos para ver esta página</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-page">
      <Header />
      <div className="progress-container">
        <section className="progress-header">
          <h1>📊 Progreso de Usuarios</h1>
          <p>Visualiza el avance de todos los estudiantes en los cursos</p>
        </section>

        <section className="progress-filters">
          <input
            type="text"
            placeholder="🔍 Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="filter-select">
            <option value="all">Todos los roles</option>
            <option value="student">Estudiantes</option>
            <option value="admin">Administradores</option>
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
            <option value="name">Ordenar por nombre</option>
            <option value="progress">Ordenar por progreso</option>
          </select>

          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              📇 Cards
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              📋 Tabla
            </button>
          </div>

          <button onClick={loadUserProgress} className="refresh-btn" disabled={loading}>
            {loading ? '⏳ Cargando...' : '🔄 Actualizar'}
          </button>
        </section>

        <section className="progress-list">
          {loading ? (
            <div className="loading-message">⏳ Cargando progreso de {users.length} usuarios...</div>
          ) : filtered.length === 0 ? (
            <div className="no-progress">
              <p>No se encontraron usuarios</p>
            </div>
          ) : viewMode === 'cards' ? (
            <div className="users-cards">
              {filtered.map((usr) => {
                const progress = userProgress[usr.id] || {
                  totalCourses: 0,
                  totalLessons: 0,
                  completedLessons: 0,
                  overallPct: 0,
                  courseProgress: {},
                };
                return (
                  <div key={usr.id} className="progress-card">
                    <div className="card-header">
                      <div className="user-info">
                        <h3>{usr.full_name || usr.email}</h3>
                        <p className="user-email">{usr.email}</p>
                      </div>
                      <span className={`role-badge role-${usr.role}`}>
                        {usr.role === 'admin' ? '👨‍💼' : '👤'}
                      </span>
                    </div>

                    <div className="card-stats">
                      <div className="stat">
                        <span className="stat-label">Cursos</span>
                        <span className="stat-value">{progress.totalCourses}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Lecciones</span>
                        <span className="stat-value">{progress.completedLessons}/{progress.totalLessons}</span>
                      </div>
                    </div>

                    <div className="card-progress">
                      <div className="progress-label">Avance General</div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress.overallPct}%` }} />
                      </div>
                      <div className="progress-percent">{progress.overallPct}%</div>
                    </div>

                    {Object.keys(progress.courseProgress).length > 0 && (
                      <div className="card-courses">
                        <div className="courses-title">Cursos:</div>
                        {Object.entries(progress.courseProgress).map(([courseId, cp]) => (
                          <div key={courseId} className="course-item">
                            <span className="course-name">{cp.title}</span>
                            <span className="course-pct">{cp.pct}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="users-table">
              <div className="table-header">
                <div className="col-user">Usuario</div>
                <div className="col-role">Rol</div>
                <div className="col-courses">Cursos</div>
                <div className="col-progress">Progreso General</div>
              </div>

              {filtered.map((usr) => {
                const progress = userProgress[usr.id] || {
                  totalCourses: 0,
                  totalLessons: 0,
                  completedLessons: 0,
                  overallPct: 0,
                };

                return (
                  <div key={usr.id} className="table-row">
                    <div className="col-user">
                      <div className="user-name">{usr.full_name || usr.email}</div>
                      <div className="user-email">{usr.email}</div>
                    </div>

                    <div className="col-role">
                      <span className={`role-badge role-${usr.role}`}>
                        {usr.role === 'admin' ? '👨‍💼 Admin' : '👤 Estudiante'}
                      </span>
                    </div>

                    <div className="col-courses">
                      <div className="course-count">
                        {progress.totalCourses} curso{progress.totalCourses !== 1 ? 's' : ''}
                      </div>
                      <div className="lesson-count">
                        {progress.completedLessons} / {progress.totalLessons} lecciones
                      </div>
                    </div>

                    <div className="col-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress.overallPct}%` }} />
                      </div>
                      <span className="progress-label">{progress.overallPct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProgressPage;
